import { createReducer } from '@reduxjs/toolkit'
import type { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'

import { artBoardActions } from './actions'
import type { ArtBoardState } from './types'
import { isArtBoardDisabled } from './utils'

const initialState: ArtBoardState = {
  arrowSource: null,
  editingArrowLabel: null,
  selectedArrowLabel: null,
  mode: 'edit',
  cursorMode: 'grab',
  socketOffline: false,
  saveFailed: false,
  aiGenerating: false,
  activityHistory: false,
  isDisabled: false,
  showStructure: false,
  showAiChat: false,
  wireframesMultiple: false,
  isEmbed: false,
  preview: null,
  activeThread: null,
  threadTarget: null,
  dragAndDrop: {
    isStarted: false,
    isActive: false,
    elementPosition: {
      x: 0,
      y: 0,
    },
    startPosition: {
      x: 0,
      y: 0,
    },
    dragged: {
      id: null,
      type: null,
      data: null,
      ids: [],
    },
    dropZone: {
      id: null,
      type: null,
      data: null,
    },
  },
}

// Any other way of selecting takes the label out of the selection. Type-only: the
// canvas reducer imports this store, and a value import here would make a cycle.
const ARROW_SELECTION_ACTIONS: string[] = [
  'SET_SELECTED_ARROW_ID',
  'RESET_SELECTED_ARROW_ID',
  'SET_SELECTED_ARROW_POINT_ID',
  'UPDATE_ARROW_POINT_OFFSET',
] satisfies `${CanvasActionTypes}`[]

export const artboardReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(artBoardActions.setEditingArrowLabel, (state, { payload }) => {
      state.editingArrowLabel = payload
    })
    .addCase(artBoardActions.setSelectedArrowLabel, (state, { payload }) => {
      state.selectedArrowLabel = payload
    })
    .addCase(artBoardActions.setSocketOffline, (state, { payload }) => {
      state.socketOffline = payload
    })
    .addCase(artBoardActions.setSaveFailed, (state, { payload }) => {
      state.saveFailed = payload
    })
    .addCase(artBoardActions.changeMode, (state, { payload }) => {
      state.mode = payload

      // Выход из режима стрелок — отмена: источник не должен пережить смену
      // контекста и подцепиться к следующему входу в режим.
      if (payload !== 'arrows') {
        state.arrowSource = null
      }
    })
    .addCase(artBoardActions.startArrows, (state, { payload }) => {
      state.mode = 'arrows'
      state.arrowSource = payload
    })
    .addCase(artBoardActions.showStructure, (state, { payload }) => {
      state.showStructure = payload.showStructure
    })
    .addCase(artBoardActions.showAiChat, (state, { payload }) => {
      state.showAiChat = payload.showAiChat
    })
    .addCase(artBoardActions.setWireframesMultiple, (state, { payload }) => {
      state.wireframesMultiple = payload
    })
    .addCase(artBoardActions.setPreview, (state, { payload }) => {
      state.preview = payload
    })
    .addCase(artBoardActions.setIsEmbed, (state) => {
      state.mode = 'readonly'
      state.isEmbed = true
    })
    .addCase(artBoardActions.dragStart, (state, { payload }) => {
      state.dragAndDrop = {
        ...initialState.dragAndDrop,
        isStarted: true,
        elementPosition: {
          x: payload.elementPosition.x,
          y: payload.elementPosition.y,
        },
        startPosition: {
          x: payload.startPosition.x,
          y: payload.startPosition.y,
        },
        dragged: {
          type: payload.dragged.type,
          id: payload.dragged.id,
          data: payload.dragged.data,
          ids: payload.dragged.ids || [],
        },
      }
    })
    .addCase(artBoardActions.dragStop, (state) => {
      state.dragAndDrop = initialState.dragAndDrop
    })
    .addCase(artBoardActions.dragSetActive, (state) => {
      state.dragAndDrop.isActive = true
    })
    .addCase(artBoardActions.dragSetDropzone, (state, { payload }) => {
      state.dragAndDrop.dropZone = {
        id: payload.id,
        type: payload.type,
        data: payload.data,
      }
    })
    .addCase(artBoardActions.dragResetDropzone, (state) => {
      state.dragAndDrop.dropZone = initialState.dragAndDrop.dropZone
    })
    .addCase(artBoardActions.setThreadTarget, (state, { payload }) => {
      state.threadTarget = payload
    })
    .addCase(artBoardActions.setAiGeneratingStatus, (state, { payload }) => {
      state.aiGenerating = payload.status
    })
    .addCase(artBoardActions.setActivityHistoryStatus, (state, { payload }) => {
      state.activityHistory = payload.status
    })
    .addCase(artBoardActions.setCursorMode, (state, { payload }) => {
      state.cursorMode = payload
    })
    .addMatcher(
      ({ type }) => ARROW_SELECTION_ACTIONS.includes(type),
      (state) => {
        state.selectedArrowLabel = null
      }
    )
    // `isDisabled` follows its facts and nothing else: whatever just changed,
    // it is worked out anew, so no action can lift a lock it does not own —
    // a change of mode used to clear the one a refused commit had set.
    .addMatcher(
      () => true,
      (state) => {
        state.isDisabled = isArtBoardDisabled(state)
      }
    )
})
